import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ChevronDown, ChevronUp, AlertTriangle, ExternalLink, ChevronLeft, ChevronRight, ArrowUp, ArrowDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import useDealUpdates from '../../hooks/useDealUpdates';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_KEY_EXPANDED = 'pfs.dataHealth.missingIntegrityExpanded';
const STORAGE_KEY_PAGE_SIZE = 'pfs.dataHealth.missingIntegrityPageSize';

// Field key → UI label (must match Summary tab tile labels exactly)
// BL-026: commission_paid swapped for commission_rate (commission is derived).
const FIELD_LABEL = {
  company_id: 'Merchant Contact Link',
  commission_rate: 'Commission Rate',
  estimated_end_date: 'Expected Payoff Date',
  broker_commission: 'Scheduled Commission',
};
const FIELD_ORDER = ['company_id', 'commission_rate', 'estimated_end_date', 'broker_commission'];

const ACTIVE_STATUSES = new Set(['Funded', 'Default', 'Defaulted', 'Payment Issues', 'In-House Collection']);

const STATUS_BADGE_CLASS = (status) => {
  if (status === 'Funded') return 'bg-green-100 text-green-800 border-green-200';
  if (status === 'Default' || status === 'Defaulted') return 'bg-red-100 text-red-800 border-red-200';
  if (status === 'Payment Issues') return 'bg-amber-100 text-amber-800 border-amber-200';
  if (status === 'In-House Collection') return 'bg-orange-100 text-orange-800 border-orange-200';
  if (status === 'Cancelled') return 'bg-slate-100 text-slate-600 border-slate-200';
  if (status && status.startsWith('Paid in Full')) return 'bg-slate-200 text-slate-700 border-slate-300';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

function readLocalStorage(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return fallback;
    return JSON.parse(v);
  } catch (_e) {
    return fallback;
  }
}

function writeLocalStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_e) { /* no-op */ }
}

export default function MissingIntegrityFieldsSection() {
  const { getAuthHeaders } = useAuth();
  const [expanded, setExpanded] = useState(() => readLocalStorage(STORAGE_KEY_EXPANDED, false));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [activeField, setActiveField] = useState(null); // null = "All"
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const v = readLocalStorage(STORAGE_KEY_PAGE_SIZE, 50);
    return PAGE_SIZE_OPTIONS.includes(v) ? v : 50;
  });
  const [sortKey, setSortKey] = useState('default'); // 'default' | 'deal_id' | 'status'
  const [sortDir, setSortDir] = useState('desc');    // 'asc' | 'desc'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/data-health/deals-missing-integrity-fields`, { headers: getAuthHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setData(d);
      setLoaded(true);
    } catch (e) {
      toast.error('Failed to load deals missing integrity fields');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Lazy-load on first expand
  useEffect(() => {
    if (expanded && !loaded && !loading) {
      load();
    }
  }, [expanded, loaded, loading, load]);

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    writeLocalStorage(STORAGE_KEY_EXPANDED, next);
  };

  const onChangePageSize = (val) => {
    const n = parseInt(val, 10);
    setPageSize(n);
    setPage(1);
    writeLocalStorage(STORAGE_KEY_PAGE_SIZE, n);
  };

  const onClickFilter = (fieldKey) => {
    setActiveField(fieldKey);
    setPage(1);
  };

  const onClickColumnSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'deal_id' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  // Filter + sort client-side
  const filteredDeals = useMemo(() => {
    if (!data) return [];
    let list = data.deals || [];
    if (activeField) {
      list = list.filter(d => (d.missing_fields || []).includes(activeField));
    }
    return list;
  }, [data, activeField]);

  const sortedDeals = useMemo(() => {
    const list = [...filteredDeals];
    const dealIdKey = (s) => {
      const str = String(s || '');
      let num = '';
      for (const c of str) {
        if (c >= '0' && c <= '9') num += c;
        else break;
      }
      return num ? parseInt(num, 10) : -1;
    };

    if (sortKey === 'default') {
      // Active first, then deal_id desc
      list.sort((a, b) => {
        const ga = a.status_group === 'active' ? 0 : 1;
        const gb = b.status_group === 'active' ? 0 : 1;
        if (ga !== gb) return ga - gb;
        return dealIdKey(b.deal_id) - dealIdKey(a.deal_id);
      });
    } else if (sortKey === 'deal_id') {
      list.sort((a, b) => {
        const diff = dealIdKey(a.deal_id) - dealIdKey(b.deal_id);
        return sortDir === 'asc' ? diff : -diff;
      });
    } else if (sortKey === 'status') {
      list.sort((a, b) => {
        const cmp = (a.status || '').localeCompare(b.status || '');
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return list;
  }, [filteredDeals, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedDeals.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageDeals = sortedDeals.slice(pageStart, pageStart + pageSize);

  // Compute insertion point for active/terminal divider on the current page
  const dividerIndex = useMemo(() => {
    if (sortKey !== 'default') return -1;
    let lastActiveIdx = -1;
    let firstTerminalIdx = -1;
    pageDeals.forEach((d, i) => {
      if (d.status_group === 'active') lastActiveIdx = i;
      else if (firstTerminalIdx === -1) firstTerminalIdx = i;
    });
    if (lastActiveIdx === -1 || firstTerminalIdx === -1) return -1;
    return firstTerminalIdx;
  }, [pageDeals, sortKey]);

  const totals = data?.totals || { total_deals: 0, deals_with_missing: 0, by_field: {} };
  const counterText = `${totals.deals_with_missing} of ${totals.total_deals}`;

  // Filter chip definitions
  const filterChips = [
    { key: null, label: 'All', count: totals.deals_with_missing },
    ...FIELD_ORDER.map(f => ({ key: f, label: FIELD_LABEL[f], count: totals.by_field?.[f] || 0 })),
  ];

  const renderPageNumbers = () => {
    const items = [];
    const maxButtons = 7;
    let pages = [];
    if (totalPages <= maxButtons) {
      pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      pages.push(1);
      const start = Math.max(2, safePage - 1);
      const end = Math.min(totalPages - 1, safePage + 1);
      if (start > 2) pages.push('…');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 1) pages.push('…');
      pages.push(totalPages);
    }
    pages.forEach((p, idx) => {
      if (p === '…') {
        items.push(<span key={`ellipsis-${idx}`} className="px-2 text-slate-400">…</span>);
      } else {
        items.push(
          <Button
            key={`page-${p}`}
            data-testid={`missing-integrity-page-${p}`}
            variant={p === safePage ? 'default' : 'outline'}
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => setPage(p)}
          >
            {p}
          </Button>
        );
      }
    });
    return items;
  };

  const Pagination = () => (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2 text-xs text-slate-600">
        <span>Page size:</span>
        <Select value={String(pageSize)} onValueChange={onChangePageSize}>
          <SelectTrigger className="h-8 w-20" data-testid="missing-integrity-page-size">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map(n => (
              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="ml-2 text-slate-500">
          Showing {sortedDeals.length === 0 ? 0 : pageStart + 1}–{Math.min(pageStart + pageSize, sortedDeals.length)} of {sortedDeals.length}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          data-testid="missing-integrity-page-prev"
          disabled={safePage <= 1}
          onClick={() => setPage(p => Math.max(1, p - 1))}
        >
          <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Prev
        </Button>
        {renderPageNumbers()}
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          data-testid="missing-integrity-page-next"
          disabled={safePage >= totalPages}
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
        >
          Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );

  const SortIcon = ({ active, dir }) => {
    if (!active) return null;
    return dir === 'asc'
      ? <ArrowUp className="inline h-3 w-3 ml-1" />
      : <ArrowDown className="inline h-3 w-3 ml-1" />;
  };

  return (
    <Card className="mt-6" data-testid="missing-integrity-section">
      <CardHeader
        className="cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={toggleExpanded}
        data-testid="missing-integrity-header-toggle"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Deals With Missing Integrity Fields
            </CardTitle>
            <CardDescription className="mt-1">
              Deals where one or more performance-tracking fields are null.
              {expanded && ' Click a deal to resolve.'}
            </CardDescription>
            <div className="mt-2">
              <Badge variant="secondary" className="font-mono text-xs" data-testid="missing-integrity-counter">
                {counterText}
              </Badge>
            </div>
          </div>
          <div className="text-slate-400">
            {expanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0">
          {/* Filter chips */}
          <div className="mb-4">
            <div className="text-xs font-medium text-slate-600 mb-2">Filter by missing field:</div>
            <div className="flex flex-wrap gap-2">
              {filterChips.map(chip => {
                const isActive = chip.key === activeField;
                const testid = chip.key === null
                  ? 'missing-integrity-filter-all'
                  : `missing-integrity-filter-${chip.key}`;
                return (
                  <button
                    key={chip.key || 'all'}
                    type="button"
                    data-testid={testid}
                    onClick={() => onClickFilter(chip.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                      isActive
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
                    }`}
                  >
                    {chip.label} <span className={`ml-1 tabular-nums ${isActive ? 'text-slate-300' : 'text-slate-500'}`}>{chip.count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Top pagination */}
          <div className="mb-3">
            <Pagination />
          </div>

          {/* Loading state */}
          {loading && (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-slate-100 animate-pulse rounded" />
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loading && sortedDeals.length === 0 && (
            <div className="text-center py-12 text-sm text-slate-500" data-testid="missing-integrity-empty">
              {totals.deals_with_missing === 0
                ? '✅ No deals are missing integrity fields. Nice work!'
                : 'No deals match the current filter.'}
            </div>
          )}

          {/* Table */}
          {!loading && sortedDeals.length > 0 && (
            <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="missing-integrity-table">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th
                      className="px-3 py-2 text-left text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-100"
                      onClick={() => onClickColumnSort('deal_id')}
                    >
                      Deal ID
                      <SortIcon active={sortKey === 'deal_id'} dir={sortDir} />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Shortcode</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Merchant</th>
                    <th
                      className="px-3 py-2 text-left text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-100"
                      onClick={() => onClickColumnSort('status')}
                    >
                      Status
                      <SortIcon active={sortKey === 'status'} dir={sortDir} />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700">Missing Fields</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 w-24">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pageDeals.map((d, idx) => (
                    <React.Fragment key={d.id}>
                      {dividerIndex === idx && (
                        <tr className="bg-slate-50">
                          <td colSpan={6} className="px-3 py-1.5 text-center text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                            ── Terminal deals sorted below ──
                          </td>
                        </tr>
                      )}
                      <tr
                        className="hover:bg-slate-50 transition-colors"
                        data-testid={`missing-integrity-row-${d.deal_id}`}
                      >
                        <td className="px-3 py-2 font-mono text-xs text-slate-900">
                          <a
                            href={`/deals/${d.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            {d.deal_id}
                          </a>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-700">{d.deal_shortcode || '—'}</td>
                        <td className="px-3 py-2 text-xs text-slate-700 max-w-xs truncate" title={d.merchant_name}>
                          {d.merchant_name || '—'}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border whitespace-nowrap ${STATUS_BADGE_CLASS(d.status)}`}>
                            {d.status || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(d.missing_fields || []).map(f => (
                              <span
                                key={f}
                                data-testid={`missing-integrity-chip-${f}-${d.deal_id}`}
                                className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-700 border border-slate-200 whitespace-nowrap"
                              >
                                {FIELD_LABEL[f] || f}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button asChild variant="link" size="sm" className="h-auto p-0 text-xs">
                            <a
                              href={`/deals/${d.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`missing-integrity-open-link-${d.deal_id}`}
                            >
                              Open <ExternalLink className="inline h-3 w-3 ml-0.5" />
                            </a>
                          </Button>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Bottom pagination */}
          {!loading && sortedDeals.length > 0 && (
            <div className="mt-3">
              <Pagination />
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
